import * as THREE from "three";
import type { JiangnanWorldModel, SurfaceKind } from "./model";

export interface ChunkBounds {
  minX: number;
  minZ: number;
  size: number;
}

const SURFACE_COLORS: Record<SurfaceKind, THREE.Color> = {
  stone: new THREE.Color(0x777c70),
  earth: new THREE.Color(0x6f6544),
  grass: new THREE.Color(0x557044),
  water: new THREE.Color(0x455f54),
};

const TERRAIN_UV_METERS = 8;
const WATER_GRID_METERS = 1.5;

function surfaceVariation(x: number, z: number): number {
  const broad = Math.sin(x * 0.173 + z * 0.119) * 0.5 + 0.5;
  const detail = Math.sin(x * -0.417 + z * 0.353) * 0.5 + 0.5;
  return broad * 0.68 + detail * 0.32;
}

export const TERRAIN_SURFACE_ORDER = ["stone", "earth", "grass", "water"] as const satisfies readonly SurfaceKind[];

export function createTerrainGeometry(
  model: JiangnanWorldModel,
  bounds: ChunkBounds,
  segments: number,
): THREE.BufferGeometry {
  const stride = segments + 1;
  const positions = new Float32Array(stride * stride * 3);
  const colors = new Float32Array(stride * stride * 3);
  const uvs = new Float32Array(stride * stride * 2);
  const indicesBySurface = new Map<SurfaceKind, number[]>(TERRAIN_SURFACE_ORDER.map((surface) => [surface, []]));

  for (let row = 0; row <= segments; row += 1) {
    for (let column = 0; column <= segments; column += 1) {
      const vertex = row * stride + column;
      const x = bounds.minX + (column / segments) * bounds.size;
      const z = bounds.minZ + (row / segments) * bounds.size;
      const point = model.getPoint(x, z);
      const color = SURFACE_COLORS[point.surface];
      const variation = surfaceVariation(x, z);
      const shade = point.surface === "stone" ? 0.82 + variation * 0.16 : 0.94 + variation * 0.08;
      positions[vertex * 3] = x;
      positions[vertex * 3 + 1] = point.height;
      positions[vertex * 3 + 2] = z;
      colors[vertex * 3] = color.r * shade;
      colors[vertex * 3 + 1] = color.g * shade;
      colors[vertex * 3 + 2] = color.b * shade;
      uvs[vertex * 2] = x / TERRAIN_UV_METERS;
      uvs[vertex * 2 + 1] = z / TERRAIN_UV_METERS;
    }
  }

  for (let row = 0; row < segments; row += 1) {
    for (let column = 0; column < segments; column += 1) {
      const a = row * stride + column;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;
      const x = bounds.minX + ((column + 0.5) / segments) * bounds.size;
      const z = bounds.minZ + ((row + 0.5) / segments) * bounds.size;
      indicesBySurface.get(model.getSurfaceKind(x, z))!.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute("uv1", new THREE.BufferAttribute(uvs.slice(), 2));
  const indices: number[] = [];
  for (const [materialIndex, surface] of TERRAIN_SURFACE_ORDER.entries()) {
    const surfaceIndices = indicesBySurface.get(surface)!;
    geometry.addGroup(indices.length, surfaceIndices.length, materialIndex);
    indices.push(...surfaceIndices);
  }
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const normals = geometry.getAttribute("normal");
  for (let vertex = 0; vertex < normals.count; vertex += 1) {
    const x = positions[vertex * 3]!;
    const z = positions[vertex * 3 + 2]!;
    if (model.getPoint(x, z).surface !== "stone") continue;
    const variation = surfaceVariation(x, z) - 0.5;
    normals.setXYZ(
      vertex,
      normals.getX(vertex) + variation * 0.032,
      normals.getY(vertex),
      normals.getZ(vertex) - variation * 0.024,
    );
  }
  geometry.normalizeNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

/** Builds the union of all main and branch canals, including water below bridge decks. */
export function createWaterGeometry(model: JiangnanWorldModel, bounds: ChunkBounds): THREE.BufferGeometry | null {
  const segments = Math.max(1, Math.round(bounds.size / WATER_GRID_METERS));
  const cellSize = bounds.size / segments;
  const vertices: number[] = [];
  const indices: number[] = [];

  for (let row = 0; row < segments; row += 1) {
    for (let column = 0; column < segments; column += 1) {
      const minX = bounds.minX + column * cellSize;
      const minZ = bounds.minZ + row * cellSize;
      const maxX = minX + cellSize;
      const maxZ = minZ + cellSize;
      const centerX = (minX + maxX) * 0.5;
      const centerZ = (minZ + maxZ) * 0.5;
      if (!model.getCanalAt(centerX, centerZ)) continue;

      const offset = vertices.length / 3;
      vertices.push(
        minX, model.getWaterSurfaceHeight(minX, minZ) + 0.018, minZ,
        maxX, model.getWaterSurfaceHeight(maxX, minZ) + 0.018, minZ,
        minX, model.getWaterSurfaceHeight(minX, maxZ) + 0.018, maxZ,
        maxX, model.getWaterSurfaceHeight(maxX, maxZ) + 0.018, maxZ,
      );
      indices.push(offset, offset + 2, offset + 1, offset + 1, offset + 2, offset + 3);
    }
  }

  if (indices.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}
